import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BookingBadge } from "../components/Badges";
import { ArrowRight, Sparkles, Shield, Mail } from "lucide-react";
import { useAuth } from "@/lib/auth";
import Seo from "../components/Seo";

export default function Home() {
   const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [artists, setArtists] = useState([]);
  const [feed, setFeed] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(null);

  const [loading, setLoading] = useState({
    stats: true,
    artists: true,
    blogs: true,
  });

  useEffect(() => {
    // Stats
    api.get("/stats")
      .then((r) => setStats(r.data))
      .catch((err) => console.error("Stats error:", err))
      .finally(() => {
        setLoading((prev) => ({ ...prev, stats: false }));
      });

    // Featured artists
    api.get("/artists")
      .then((r) => setArtists(r.data.slice(0, 3)))
      .catch((err) => console.error("Artists error:", err))
      .finally(() => {
        setLoading((prev) => ({ ...prev, artists: false }));
      });

    // Community feed
    api.get("/community/posts")
      .then((r) => setFeed(r.data.slice(0, 3)))
      .catch((err) => console.error("Feed error:", err));

    // Blog posts
    api.get("/blog/posts")
      .then((r) => setBlogs(r.data.slice(0, 7)))
      .catch((err) => console.error("Blogs error:", err))
      .finally(() => {
        setLoading((prev) => ({ ...prev, blogs: false }));
      });
  }, []);


  const getMiddleIndex = (length) => {
    if (length === 0) return 0;

    return Math.floor(length / 2);
  };


  // Update center when blogs are loaded or changed
  useEffect(() => {
    setCurrentIndex(getMiddleIndex(blogs.length));
  }, [blogs.length]);

  useEffect(() => {
    if (blogs.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % blogs.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [blogs.length]);

  return (
    <div data-testid="home-page">
      <Seo/>
      {/* Hero */}
      <section className="container-x pt-16 md:pt-24 pb-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs tracking-widest uppercase text-black/50 mb-4">A community for the tattoo world</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight">
              Ink, considered.<br />
              A clean home for artists & collectors.
            </h1>
            <p className="text-base md:text-lg text-black/70 mt-6 max-w-xl">
              Discover artists, book sessions, share inspiration, follow flash drops, and write the field guide future first-timers will read.
            </p>
            <div className="flex gap-3 mt-8 flex-wrap">
              {user ? null :<Link to="/sign-up" className="btn-filled" data-testid="hero-cta-join">Join Inkstead <ArrowRight size={14} /></Link>}
              <Link to="/explore" className="btn-outline" data-testid="hero-cta-explore">Browse the gallery</Link>
            </div>
          </div>
          <div className="aspect-[4/5] overflow-hidden rounded-[12px] border-[0.5px] border-black/10">
            <img src="https://images.pexels.com/photos/6593369/pexels-photo-6593369.jpeg" alt="Tattoo studio" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="w-full border-y-[0.5px] border-black/10 py-10">
        <div className="w-full max-w-7xl mx-auto px-6 grid grid-cols-4 gap-8">

          {loading.stats ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="h-10 w-20 bg-neutral-200 rounded mb-2" />
                <div className="h-4 w-28 bg-neutral-100 rounded" />
              </div>
            ))
          ) : (
            [
              { label: "Artists", v: stats.artists || 0 },
              { label: "Portfolios", v: stats.portfolios || 0 },
              { label: "Community posts", v: stats.posts || 0 },
              { label: "Stories", v: stats.blogs || 0 },
            ].map((s) => (
              <div
                key={s.label}
                data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-center"
              >
                <div className="font-serif text-4xl">{s.v}</div>
                <div className="text-sm text-black/60 mt-1">{s.label}</div>
              </div>

            ))
          )}
        </div>
      </section>

      {/* Featured artists */}
      <section className="container-x py-16">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-serif text-3xl md:text-4xl">Featured artists</h2>
          <Link to="/artists" className="text-sm underline underline-offset-4">All artists →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {loading.artists ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="card animate-pulse"
              >
                {/* Image skeleton */}
                <div className="aspect-square w-full rounded-[8px] mb-4 bg-neutral-200" />

                {/* Name and booking status */}
                <div className="flex justify-between items-center">
                  <div className="h-5 w-32 bg-neutral-200 rounded" />
                  <div className="h-5 w-20 bg-neutral-100 rounded-full" />
                </div>

                {/* Location */}
                <div className="h-4 w-24 bg-neutral-100 rounded mt-3" />

                {/* Tags */}
                <div className="flex gap-2 mt-4">
                  <div className="h-6 w-16 bg-neutral-100 rounded-full" />
                  <div className="h-6 w-20 bg-neutral-100 rounded-full" />
                  <div className="h-6 w-14 bg-neutral-100 rounded-full" />
                </div>
              </div>
            ))
          ) : (
            artists.map((a) => (
              <Link
                key={a.id}
                to={`/artists/${a.username}`}
                className="card hover:-translate-y-0.5 transition-transform"
                data-testid={`featured-artist-${a.username}`}
              >
                <div className="aspect-square w-full overflow-hidden rounded-[8px] mb-4 bg-neutral-100">
                  {a.avatar_url && (
                    <img
                      src={a.avatar_url}
                      alt={a.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-serif text-xl">{a.name}</h3>
                  <BookingBadge status={a.booking_status} />
                </div>

                <p className="text-sm text-black/60 mt-1">{a.location}</p>

                <div className="flex flex-wrap gap-2 mt-3">
                  {(a.style_tags || []).slice(0, 3).map((t) => (
                    <span key={t} className="badge badge-neutral">
                      {t}
                    </span>
                  ))}
                </div>
              </Link>
            ))
          )}
        </div>
      </section>

      {/* Community + Blog preview */}
      <section className="container-x py-12 grid md:grid-cols-1 gap-12">
        {/* <div>
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif text-2xl md:text-3xl">From the community</h2>
            <Link to="/community" className="text-sm underline underline-offset-4">Open feed →</Link>
          </div>
          <div className="space-y-4">
            {feed.map((p) => (
              <div key={p.id} className="card" data-testid={`feed-preview-${p.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{p.author_name}</span>
                  <span className="badge badge-neutral">{p.post_type}</span>
                </div>
                <p className="text-sm text-black/80">{p.body}</p>
              </div>
            ))}
          </div>
        </div> */}
        {/* <div>
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif text-2xl md:text-3xl">Stories</h2>
            <Link to="/blog" className="text-sm underline underline-offset-4">All stories →</Link>
          </div>
          <div className="space-y-4">
            {blogs.map((b) => (
              <Link key={b.id} to={`/blog/${b.slug}`} className="card block hover:-translate-y-0.5 transition-transform" data-testid={`blog-preview-${b.slug}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="badge badge-neutral">{b.category}</span>
                  {b.author_account_type === "artist" && <span className="badge badge-flash">Artist</span>}
                </div>
                <h3 className="font-serif text-xl">{b.title}</h3>
                <p className="text-sm text-black/60 mt-1">{b.author_name} · {b.read_time} min read</p>
              </Link>
            ))}
          </div>
        </div> */}

        <div className="w-full overflow-hidden">
          {/* Header */}
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif text-2xl md:text-3xl">
              The Inkstead Journal
            </h2>

            <Link
              to="/blog"
              className="text-sm underline underline-offset-4"
            >
              Explore all →
            </Link>
          </div>

          {/* Carousel viewport */}
          {/* Carousel viewport */}
          <div className="relative overflow-hidden py-10 min-h-[300px]">

            {loading.blogs ? (
              <div className="flex items-center justify-center gap-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div
                    key={index}
                    className={`
            flex-shrink-0 w-[280px] card animate-pulse
            ${index === 1
                        ? "shadow-xl scale-100"
                        : "scale-90 opacity-50"
                      }
          `}
                  >
                    {/* Cover image skeleton */}
                    <div className="w-full h-40 bg-neutral-200 rounded-lg mb-4" />

                    {/* Category */}
                    <div className="flex gap-2 mb-3">
                      <div className="h-5 w-24 bg-neutral-200 rounded-full" />
                      <div className="h-5 w-14 bg-neutral-100 rounded-full" />
                    </div>

                    {/* Title */}
                    <div className="h-6 w-full bg-neutral-200 rounded mb-2" />
                    <div className="h-6 w-3/4 bg-neutral-100 rounded mb-3" />

                    {/* Author */}
                    <div className="h-4 w-36 bg-neutral-100 rounded" />
                  </div>
                ))}
              </div>
            ) : blogs.length > 0 ? (
              <div
                className="flex items-center gap-4 transition-transform duration-700 ease-in-out"
                style={{
                  transform: `translateX(calc(50% - ${currentIndex * 296 + 140}px))`,
                }}
              >
                {blogs.map((b, index) => {
                  const isActive = index === currentIndex;

                  return (
                    <Link
                      key={b.id}
                      to={`/blog/${b.slug}`}
                      data-testid={`blog-preview-${b.slug}`}
                      className={`
              flex-shrink-0 w-[280px] card block overflow-hidden
              transition-all duration-700 ease-in-out
              ${isActive
                          ? "scale-100 opacity-100 shadow-2xl z-10"
                          : "scale-90 opacity-50 shadow-sm"
                        }
            `}
                    >
                      {b.cover_image_url ? (
                        <img
                          src={b.cover_image_url}
                          alt={b.title}
                          className="w-full h-40 object-cover rounded-lg mb-4"
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className="w-full h-40 rounded-lg mb-4 bg-neutral-100 flex items-center justify-center text-sm text-black/40"
                          data-testid={`blog-preview-noimage-${b.slug}`}
                        >
                          No image
                        </div>
                      )}

                      <div className="flex items-center gap-2 mb-2">
                        <span className="badge badge-neutral">
                          {b.category}
                        </span>

                        {b.author_account_type === "artist" && (
                          <span className="badge badge-flash">
                            Artist
                          </span>
                        )}
                      </div>

                      <h3 className="font-serif text-xl">{b.title}</h3>

                      <p className="text-sm text-black/60 mt-1">
                        {b.author_name} · {b.read_time} min read
                      </p>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="flex justify-center items-center min-h-[200px] text-sm text-black/50">
                No stories available yet.
              </div>
            )}
          </div>

          {/* Pagination - only after loading */}
          {!loading.blogs && blogs.length > 1 && (
            <div className="flex justify-center gap-2 mt-2">
              {blogs.map((b, index) => (
                <button
                  key={b.id}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Go to story ${index + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${currentIndex === index
                    ? "w-6 bg-black"
                    : "w-2 bg-black/20"
                    }`}
                />
              ))}
            </div>
          )}

        </div>
      </section>
    </div>
  );
}
